/**
 * Game Scene Setup
 * Loads scene from server or falls back to hardcoded prototype values
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { Engine } from '../../engine/Engine';
import { renderScheduler } from '../../engine/render/RenderScheduler';
import { sceneApi } from '../../api/sceneApi';
import { serverConfig } from '../../network/ServerConfig';
import {
  SceneLoader,
  buildSceneBodies,
  SDL_COLLISION_GROUPS,
  SDL_COLLISION_MASKS,
  type SDLCameraAnchor,
  type SDLSceneDocument,
  type GameModeId,
} from '@sdl/scene-format';

export interface GameSceneConfig {
  tableWidth?: number;
  tableDepth?: number;
  wallHeight?: number;
  gameModeId?: GameModeId;
}

const DEFAULT_CONFIG = {
  tableWidth: 12,
  tableDepth: 16,
  wallHeight: 8,
};

/** A light's resolved state for one phase (or its base/shooting pose). */
export interface LightStateSnapshot {
  color: string;
  intensity: number;
  position: [number, number, number];
  target?: [number, number, number];
  distance?: number;
  decay?: number;
  angle?: number;
  penumbra?: number;
  castShadow: boolean;
  visible: boolean;
}

/** A light with optional per-anchor overrides (from editor scene format) */
export interface AnchoredLight {
  light: THREE.Light;
  /** The load-time (doc/shooting) pose, captured ONCE while the light is at its
   *  document values — before any anchor override is applied. This is the
   *  authoritative 'shooting' base; never re-sample it from the live light (it
   *  may be sitting at the betting anchor by the time consumers read it). */
  base: LightStateSnapshot;
  anchors?: Record<string, {
    color?: string;
    intensity?: number;
    position?: [number, number, number];
    target?: [number, number, number];
    distance?: number;
    decay?: number;
    angle?: number;
    penumbra?: number;
    castShadow?: boolean;
    visible?: boolean;
  }>;
}

/** Snapshot a light's current state as a base/shooting pose. Call at scene load. */
function captureLightBase(light: THREE.Light): LightStateSnapshot {
  const p = light.position;
  const l = light as THREE.Light & {
    color?: THREE.Color; target?: THREE.Object3D;
    distance?: number; decay?: number; angle?: number; penumbra?: number;
  };
  const base: LightStateSnapshot = {
    color: '#' + (l.color?.getHexString?.() ?? 'ffffff'),
    intensity: light.intensity,
    position: [p.x, p.y, p.z],
    castShadow: light.castShadow,
    visible: light.visible,
  };
  if (l.distance !== undefined) base.distance = l.distance;
  if (l.decay !== undefined) base.decay = l.decay;
  if (l.angle !== undefined) base.angle = l.angle;
  if (l.penumbra !== undefined) base.penumbra = l.penumbra;
  if (l.target?.position) {
    const t = l.target.position;
    base.target = [t.x, t.y, t.z];
  }
  return base;
}

/**
 * Snap a set of anchored lights to the given phase's resolved state. The single
 * source of truth for shooting↔betting light state — used by GameFlowController
 * (phase transitions) AND by the resident-scene consumers (try-dice scenes)
 * that reuse the shared lights and must reset them to 'shooting' on entry.
 *
 * 'shooting' is each light's captured base; 'betting' layers the editor's
 * per-anchor overrides on top, falling back to base for anything unset.
 *
 * CONSTANT-COUNT RULE (do not "optimize" by toggling visible per phase): a
 * material's shader program keys on the COUNT of visible lights per type — and
 * of visible + shadow-casting lights per type. Flipping light.visible or
 * light.castShadow between phases changes that count and forces EVERY material
 * to recompile its shader, a GPU spike that loses the WebGL context on WKWebView
 * under load. So visible + castShadow stay CONSTANT (the union across phases: a
 * light stays in the scene if ANY phase uses it) and a phase "turns off" a light
 * purely via intensity = 0 (a free uniform).
 */
export function applyAnchorToLights(
  anchoredLights: AnchoredLight[],
  anchorName: 'shooting' | 'betting'
): void {
  for (const { light, base, anchors } of anchoredLights) {
    if (!base) continue;

    const config = anchorName === 'shooting' ? undefined : anchors?.[anchorName];

    const phaseOn = config?.visible ?? base.visible;
    const phaseIntensity = config?.intensity ?? base.intensity;

    let everVisible = base.visible;
    let everShadow = base.castShadow;
    if (anchors) {
      for (const a of Object.values(anchors)) {
        everVisible = everVisible || (a.visible ?? base.visible);
        everShadow = everShadow || (a.castShadow ?? base.castShadow);
      }
    }

    // Continuous props — snap straight to the resolved value (all uniforms, so
    // changing them never recompiles: color, position, target, distance…).
    (light as any).color?.set(config?.color ?? base.color);
    const pos = config?.position ?? base.position;
    light.position.set(pos[0], pos[1], pos[2]);

    const tgt = config?.target ?? base.target;
    if ((light as any).target?.position && tgt) {
      (light as any).target.position.set(tgt[0], tgt[1], tgt[2]);
      (light as any).target.updateMatrixWorld?.();
    }

    if ((light as any).distance !== undefined) (light as any).distance = config?.distance ?? base.distance;
    if ((light as any).decay !== undefined) (light as any).decay = config?.decay ?? base.decay;
    if ((light as any).angle !== undefined) (light as any).angle = config?.angle ?? base.angle;
    if ((light as any).penumbra !== undefined) (light as any).penumbra = config?.penumbra ?? base.penumbra;

    // visible + castShadow are CONSTANT across phases (the union). The phase's
    // on/off is expressed entirely through intensity (0 = off).
    light.visible = everVisible;
    light.castShadow = everShadow;
    light.intensity = phaseOn ? phaseIntensity : 0;
  }

  // Light position/target/angle snaps change the shadow frustum — rebuild the
  // on-demand shadow map (see RenderScheduler.shouldRenderShadows).
  renderScheduler.markShadowsDirty();
}

export class GameScene {
  private engine: Engine;
  private config: GameSceneConfig & typeof DEFAULT_CONFIG;
  private shootingAnchor = new THREE.Object3D();
  private bettingAnchor = new THREE.Object3D();
  private sceneLoader: SceneLoader | null = null;
  private loadedFromServer = false;
  private anchoredLights: AnchoredLight[] = [];
  // The SceneLoader-created group added to engine.scene. Held so dispose() can
  // free its per-instance geometries / materials / light shadow maps — every
  // object inside was created for THIS scene, so it's safe to dispose (unlike a
  // blind engine.scene traverse, which would hit shared dice geometry/materials).
  private loadedGroup: THREE.Object3D | null = null;
  // Static physics bodies this scene added to the world (floor / wall / scene
  // colliders). Tracked so dispose() removes exactly them on a scene swap —
  // buildSceneBodies adds them via raw world.addBody (untracked by PhysicsWorld),
  // so physics.dispose() would otherwise leave them behind.
  private staticBodies: CANNON.Body[] = [];
  // The loaded scene document id (SceneManager keys residency on this).
  private sceneId: string | null = null;
  // Environment objects in engine.scene (snapshot at init, before any actors).
  private persistentObjects: THREE.Object3D[] = [];

  constructor(engine: Engine, config?: GameSceneConfig) {
    this.engine = engine;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Initialize the scene - tries server first, falls back to hardcoded
   */
  async initialize(preloadedDoc?: SDLSceneDocument): Promise<void> {
    // Snapshot world bodies so we can record exactly the static bodies this
    // scene adds during load (server path via buildSceneBodies, or fallback
    // floor/wall). Only scene statics are added here — dice are created later.
    const bodiesBefore = new Set(this.engine.physics.world.bodies);
    try {
      const isBaked = await this.loadFromServer(preloadedDoc);
      this.loadedFromServer = true;
      if (isBaked) {
      } else {
      }
    } catch (err) {
      console.warn('⚠️ Failed to load scene from server, using fallback:', err);
      this.setupFallbackScene();
    }
    this.staticBodies = this.engine.physics.world.bodies.filter((b) => !bodiesBefore.has(b));

    // Snapshot the scene's own objects (loaded group, anchors, fallback lights/
    // floor/wall) as "persistent". At this point no actors (dice, betting) exist
    // yet, so engine.scene contains only the environment. Game teardown clears
    // everything EXCEPT these so the resident environment survives for reuse.
    this.persistentObjects = [...this.engine.scene.children];
  }

  /** The scene's own objects (environment), to be kept across actor teardown. */
  getPersistentObjects(): THREE.Object3D[] {
    return this.persistentObjects;
  }

  /**
   * Load scene from the server using SceneLoader
   * @returns true if a baked (optimized) scene was loaded
   */
  private async loadFromServer(preloadedDoc?: SDLSceneDocument): Promise<boolean> {
    const document = preloadedDoc
      ?? (this.config.gameModeId
        ? await sceneApi.getForGameMode(this.config.gameModeId)
        : await sceneApi.getDefault());
    this.sceneId = document.metadata?.id ?? null;

    // Create SceneLoader with GLTFLoader for baked scene support
    this.sceneLoader = new SceneLoader({
      gltfLoader: new GLTFLoader(),
      baseUrl: serverConfig.mainServerUrl,
      preferBaked: true,  // Use baked scene if available
      maxAnisotropy: this.engine.renderer.renderer.capabilities.getMaxAnisotropy(),
    });
    const loaded = await this.sceneLoader.load(document);

    const scene = this.engine.scene;

    // Setup background from document
    if (document.settings.background.type === 'color' && document.settings.background.color) {
      scene.background = new THREE.Color(document.settings.background.color);
    }

    // Apply scene's physics gravity so client matches server. The server reads
    // document.settings.physics.gravity when constructing HiLoPhysicsWorld;
    // if we don't mirror that here, HI_LO dice fall at different rates on
    // server vs client whenever the scene's gravity differs from the default.
    if (document.settings?.physics?.gravity !== undefined) {
      this.engine.physics.setGravity(document.settings.physics.gravity);
    }

    // No fog - keep it clean like prototype
    scene.fog = null;

    // Add loaded objects to scene
    scene.add(loaded.scene as any);
    this.loadedGroup = loaded.scene as unknown as THREE.Object3D;

    // NOTE: static-scene mesh merge was reverted here — it regressed wallet /
    // bet-zone placement (raycastGroundY in BettingController casts down onto the
    // scene's ground meshes, and folding the per-tile meshes into one merged mesh
    // changed what that raycast resolves). Re-add a ground-raycast-safe version
    // later to reclaim the draw-call + pavement-cull win.

    this.sceneLoader?.loadDecals(document.decals ?? [], scene as any);

    // Create physics bodies for loaded objects via shared builder.
    // The builder lives in @sdl/scene-format so the game-server's HI_LO
    // physics world uses identical geometry — critical for reconciliation.
    buildSceneBodies(this.engine.physics.world, document, {
      tableMaterial: this.engine.physics.tableMaterial,
      wallMaterial: this.engine.physics.wallMaterial,
    });

    // Setup camera anchors from document
    this.setupCameraAnchorsFromDocument(document.cameraAnchors);

    // Collect lights with anchor data for phase-based lighting
    this.collectAnchoredLights(scene);

    // Set initial camera position from first anchor
    const camera = this.engine.camera;
    const defaultAnchor = document.cameraAnchors.find(a => a.name === 'Shooting');
    if (defaultAnchor) {
      camera.position.set(...defaultAnchor.position);
      camera.lookAt(...defaultAnchor.lookAt);
    } else {
      camera.position.set(0, 14, 10);
      camera.lookAt(0, 0, 0);
    }

    // Extract table dimensions from floor object for compatibility
    const floor = document.objects.find(o => o.name === 'Floor');
    if (floor && floor.type === 'primitive' && floor.primitive.shape === 'plane') {
      this.config.tableWidth = floor.primitive.parameters.planeWidth;
      this.config.tableDepth = floor.primitive.parameters.planeHeight;
    }

    const wall = document.objects.find(o => o.name === 'Back Wall');
    if (wall && wall.type === 'primitive' && wall.primitive.shape === 'box') {
      this.config.wallHeight = wall.primitive.parameters.height;
    }

    // Raycast from betting anchor to find actual ground hit point for zone positioning
    this.raycastBettingAnchorGround(document.cameraAnchors);

    return loaded.isBaked ?? false;
  }

  /**
   * Setup camera anchors from document
   */
  private setupCameraAnchorsFromDocument(anchors: SDLCameraAnchor[]): void {
    const shootingAnchor = anchors.find(a => a.name === 'Shooting');
    const bettingAnchor = anchors.find(a => a.name === 'Betting');

    if (shootingAnchor) {
      this.shootingAnchor.name = 'Anchor.Shooting';
      this.shootingAnchor.position.set(...shootingAnchor.position);
      this.shootingAnchor.userData.lookAt = shootingAnchor.lookAt;
      this.shootingAnchor.userData.fov = shootingAnchor.fov;
    } else {
      this.shootingAnchor.name = 'Anchor.Shooting';
      this.shootingAnchor.position.set(0, 14, 10);
    }

    if (bettingAnchor) {
      this.bettingAnchor.name = 'Anchor.Betting';
      this.bettingAnchor.position.set(...bettingAnchor.position);
      this.bettingAnchor.userData.lookAt = bettingAnchor.lookAt;
      this.bettingAnchor.userData.fov = bettingAnchor.fov;
    } else {
      this.bettingAnchor.name = 'Anchor.Betting';
      this.bettingAnchor.position.set(0, 0.01, -0.8);
    }

    this.engine.scene.add(this.shootingAnchor);
    this.engine.scene.add(this.bettingAnchor);
  }

  /**
   * Optimize shadows for mobile devices
  /**
   * Fallback: Setup scene exactly like prototype (hardcoded)
   */
  private setupFallbackScene(): void {
    this.setupScene();
    this.setupLighting();
    this.setupFloor();
    this.setupWall();
    this.setupAnchors();
  }

  /**
   * Setup scene exactly like prototype
   */
  private setupScene(): void {
    const scene = this.engine.scene;

    // Background color - exactly like prototype
    scene.background = new THREE.Color(0x1a1a1a);

    // No fog - prototype doesn't use fog
    scene.fog = null;

    // Camera position - exactly like prototype
    const camera = this.engine.camera;
    camera.position.set(0, 14, 10);
    camera.lookAt(0, 0, 0);
  }

  /**
   * Setup lighting exactly like prototype
   */
  private setupLighting(): void {
    const scene = this.engine.scene;

    // Clear any existing lights
    scene.children
      .filter((c) => c instanceof THREE.Light)
      .forEach((l) => scene.remove(l));

    // Ambient light - prototype: 0xffffff, 0.4
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // Main directional light - prototype values
    const mainLight = new THREE.DirectionalLight(0xffffff, 1.2);
    mainLight.position.set(5, 15, 5);
    mainLight.castShadow = true;
    // 1024 to match the 'high' quality tier (re-applied after scene load via
    // engine.setQuality). 512 was too boxy for the close shooting camera; 1024
    // doubles shadow texel density at ~4 MB depth VRAM (safe now that the PMREM
    // env-map hog behind the context loss is gone).
    mainLight.shadow.mapSize.width = 1024;
    mainLight.shadow.mapSize.height = 1024;
    mainLight.shadow.camera.near = 1;
    mainLight.shadow.camera.far = 30;
    mainLight.shadow.camera.left = -15;
    mainLight.shadow.camera.right = 15;
    mainLight.shadow.camera.top = 15;
    mainLight.shadow.camera.bottom = -15;
    // No shadow bias: it eats the contact shadow when a die rests on the floor.
    // The pip shadow-acne that motivated a bias is solved at the source by the
    // dice being receiveShadow=false, and the flat floor (lit near head-on) has
    // no acne of its own — so default bias (0) gives clean contact shadows.
    scene.add(mainLight);

    // Rim light - prototype: 0xffd700, 0.3
    const rimLight = new THREE.DirectionalLight(0xffd700, 0.3);
    rimLight.position.set(-5, 10, -5);
    scene.add(rimLight);
  }

  /**
   * Setup floor exactly like prototype
   */
  private setupFloor(): void {
    const { tableWidth, tableDepth } = this.config;
    const scene = this.engine.scene;
    const physics = this.engine.physics;

    // Floor geometry - exactly like prototype
    const floorGeometry = new THREE.PlaneGeometry(tableWidth, tableDepth);

    // Floor material - prototype: 0x1a472a, roughness 0.9, metalness 0
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a472a,
      roughness: 0.9,
      metalness: 0.0,
    });

    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // Floor physics - static plane exactly like prototype
    const floorShape = new CANNON.Plane();
    const floorBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: floorShape,
      material: physics.tableMaterial,
      collisionFilterGroup: SDL_COLLISION_GROUPS.TABLE,
      collisionFilterMask: SDL_COLLISION_MASKS.TABLE,
    });
    floorBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    physics.world.addBody(floorBody);

  }

  /**
   * Setup wall exactly like prototype - ONLY back wall
   */
  private setupWall(): void {
    const { tableWidth, tableDepth, wallHeight } = this.config;
    const scene = this.engine.scene;
    const physics = this.engine.physics;

    const wallThickness = 0.5;
    const wallColor = 0x3d2817;

    const wallWidth = tableWidth + wallThickness * 2;

    const wallGeometry = new THREE.BoxGeometry(wallWidth, wallHeight, wallThickness);
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: wallColor,
      roughness: 0.7,
      metalness: 0.2,
    });

    const wall = new THREE.Mesh(wallGeometry, wallMaterial);
    wall.position.set(0, wallHeight / 2, -tableDepth / 2 - wallThickness / 2);
    wall.castShadow = true;
    wall.receiveShadow = true;
    scene.add(wall);

    // Wall physics
    const wallShape = new CANNON.Box(
      new CANNON.Vec3(wallWidth / 2, wallHeight / 2, wallThickness / 2)
    );
    const wallBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: wallShape,
      material: physics.wallMaterial,
      collisionFilterGroup: SDL_COLLISION_GROUPS.WALL,
      collisionFilterMask: SDL_COLLISION_MASKS.WALL,
    });
    wallBody.position.set(0, wallHeight / 2, -tableDepth / 2 - wallThickness / 2);
    physics.world.addBody(wallBody);
  }

  private setupAnchors(): void {
    this.shootingAnchor.name = 'Anchor.Shooting';
    this.shootingAnchor.position.set(0, 0.01, 0);

    this.bettingAnchor.name = 'Anchor.Betting';
    this.bettingAnchor.position.set(0, 0.01, -0.8);

    this.engine.scene.add(this.shootingAnchor);
    this.engine.scene.add(this.bettingAnchor);
  }

  /**
   * Walk the scene and collect all lights that have sdlAnchors userData
   * (set by SceneLoader from the document's anchors field).
   */
  private collectAnchoredLights(scene: THREE.Scene): void {
    this.anchoredLights = [];
    scene.traverse((obj) => {
      if (obj instanceof THREE.Light) {
        const anchors = obj.userData.sdlAnchors;
        // Capture base NOW — at load the light is at its doc/shooting pose, before
        // any phase anchor override. This is the authoritative shooting base.
        this.anchoredLights.push({ light: obj, base: captureLightBase(obj), anchors: anchors ?? undefined });
      }
    });
    if (this.anchoredLights.length > 0) {
    }
  }

  /**
   * Get all lights with anchor data for phase-based control
   */
  getAnchoredLights(): AnchoredLight[] {
    return this.anchoredLights;
  }

  /**
   * Reset the scene's anchored lights to their shooting (base) pose. The resident
   * scene is shared (SceneManager) and a prior game's betting phase can leave the
   * lights at the betting anchor, so any non-betting consumer that reuses the
   * scene (the try-dice flows) must call this on entry to restore shooting light.
   */
  applyShootingLights(): void {
    applyAnchorToLights(this.anchoredLights, 'shooting');
  }

  getCameraAnchors(): { shooting: THREE.Object3D; betting: THREE.Object3D } {
    return { shooting: this.shootingAnchor, betting: this.bettingAnchor };
  }

  /** The loaded scene document id (null if it fell back to the hardcoded scene). */
  getSceneId(): string | null {
    return this.sceneId;
  }

  /**
   * Get table bounds for camera calculations
   */
  getTableBounds(): { width: number; depth: number } {
    return {
      width: this.config.tableWidth,
      depth: this.config.tableDepth,
    };
  }

  /**
   * Get betting element positions
   */
  getBettingPositions(): {
    wallet: { x: number; y: number; z: number };
    bettingZone: { x: number; y: number; z: number };
  } {
    return {
      wallet: { x: 2, y: 1.8, z: 3 },
      bettingZone: { x: 0, y: 0.01, z: 0 },
    };
  }

  /**
   * Whether the scene was loaded from server or using fallback
   */
  isLoadedFromServer(): boolean {
    return this.loadedFromServer;
  }

  /**
   * Update scene (for animated elements)
   */
  update(_delta: number): void {
    // Nothing to update - simple static scene like prototype
  }

  /**
   * Debug: raycast from betting anchor camera position toward lookAt
   * to see what scene objects are in the line of sight
   */
  /**
   * Raycast from the betting anchor camera position through its lookAt direction
   * to find the actual ground hit point. Stores result on bettingAnchor.userData.groundHit
   * so GameFlowController can use it for zone positioning.
   */
  private raycastBettingAnchorGround(anchors: SDLCameraAnchor[]): void {
    const bettingAnchor = anchors.find(a => a.name === 'Betting');
    if (!bettingAnchor) return;

    // Force world matrix update — meshes just added may not have current transforms
    this.engine.scene.updateMatrixWorld(true);

    const origin = new THREE.Vector3(...bettingAnchor.position);
    const lookAt = new THREE.Vector3(...bettingAnchor.lookAt);
    const direction = new THREE.Vector3().subVectors(lookAt, origin).normalize();

    const allMeshes: THREE.Mesh[] = [];
    this.engine.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) allMeshes.push(obj);
    });

    const raycaster = new THREE.Raycaster(origin, direction);
    const hits = raycaster.intersectObjects(allMeshes, false);

    if (hits.length > 0) {
      const hit = hits[0];
      this.bettingAnchor.userData.groundHit = [hit.point.x, hit.point.y, hit.point.z];
    } else {
      console.warn('🎯 Betting anchor raycast missed all geometry — falling back to lookAt XZ');
    }
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    // Free the per-instance GPU resources of the loaded scene. SceneLoader.dispose()
    // only frees its texture cache; geometries, materials and (the big one on iOS)
    // directional/spot-light shadow-map render targets would otherwise leak on every
    // teardown — that accumulation is what blacked out / crashed the WKWebView when
    // the isolated "try dice" scene is built and torn down repeatedly.
    if (this.loadedGroup) {
      this.loadedGroup.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = (mesh as unknown as { material?: THREE.Material | THREE.Material[] }).material;
        if (mat) (Array.isArray(mat) ? mat : [mat]).forEach((m) => {
          // Dispose every texture the material references (map, lightMap, normalMap,
          // etc.). Most are also in SceneLoader's cache (idempotent double-dispose is
          // safe), but the baked lightmap is NOT cached — this catches it.
          for (const key in m) {
            const val = (m as unknown as Record<string, unknown>)[key] as { isTexture?: boolean; dispose?: () => void } | null;
            if (val && val.isTexture && typeof val.dispose === 'function') val.dispose();
          }
          m.dispose();
        });

        const light = obj as THREE.Light & { shadow?: THREE.LightShadow };
        if (light.isLight && light.shadow?.map) {
          light.shadow.map.dispose();
          light.shadow.map = null as unknown as THREE.WebGLRenderTarget;
        }
      });
      this.loadedGroup.parent?.remove(this.loadedGroup);
      this.loadedGroup = null;
    }

    if (this.sceneLoader) {
      this.sceneLoader.dispose();  // texture cache
      this.sceneLoader = null;
    }

    // Remove the static physics bodies this scene added (floor/wall/colliders).
    for (const body of this.staticBodies) {
      this.engine.physics.world.removeBody(body);
    }
    this.staticBodies = [];

    // Remove the (empty) camera anchor markers so they don't accumulate on swap.
    this.engine.scene.remove(this.shootingAnchor);
    this.engine.scene.remove(this.bettingAnchor);

    this.anchoredLights = [];
    this.sceneId = null;
  }
}
